import {
  BigNumberish, BigNumber, ethers, ContractInterface,
} from 'ethers'
import { Contract } from '@ethersproject/contracts'
import { Provider, Web3Provider } from '@ethersproject/providers'

import localForage from 'localforage'

import { formatUnits } from 'ethers/lib/utils'
import { DEFAULT_TOKEN_SYMBOL, DEX_TOKEN_LIST_BASE_URI, BRIDGE_TOKEN_LIST_BASE_URI } from './constants'
import { ChainData } from './types'
import { supportedChains } from './chains'
import { Currency, Token } from '../entities'

export function getChainData(chainId?: number): ChainData | undefined {
  const chainData = supportedChains.filter(
    (chain: ChainData) => chain.chainId === chainId,
  )[0]

  return chainData
}

export function getShortAddress(address: string): string {
  return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`
}

export function prettyFormat(value: BigNumberish, unitName?: number, points = 8): number {
  return +((+formatUnits(value, unitName)).toFixed(points))
}

export function getDumbLogoURI(): string {
  // eslint-disable-next-line max-len
  return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAQwElEQVRoQ9WZfYwc9XnHPzOzs7Oz73vva5/vjvM75s0EA8aAgQRCIJQU0jq0DiEhihIhtQlBqVArFCWgVm2SBiUqpZQ2DQEURAmIkMQESAjvLw4QjO347MPn8/nefG97uzs779VvXs7rF7Dhr2ak0e7e7M1+v8/zfV5H8n3f50MfPtWRbdijr6JMPoXm7SChjKCk6pD0QIlu7AKWhGdqOHYXlrQCq+WjJBadR3bxaciJ5IdGIH0YAr7nMLfrGXj3IbLVx0iUpqGoQr4FMotAXwqJdpB1QALPBGcGzHehtg8qUzBbx5vJUNU+gb3kOgqrPk5CS39gIh+YwPzQ67hvfZ+C8RBShw3tHdCyFvIXgbYaEj0glQEBXo4AecIF4E+COwzWHqg+D9MvwcRemIA5+ZM4J99M66qLPxCJEybg2g2mXrqb0tAdqG2TUG6FrkuhdBVopwGtIWDfA19opvmIVCrJIMW6mgdnN8xtgbHHYWQQdzzJbOct5DbcQjJTOiEiJ0TAmB3D+M03aDHug0XAkgtg0WbQzwY08IR1XZASkdUFSGF9KQIhCAgvCGJu9F1xWQu/Yv8Rxh+CocdgpMGsfwXKxu+T61p+XBLHJVA9uA/nyRspJp6CxWno3wTl64B2oHHoBzwffIEmBn8sAoKEExFxQm9JHsip8HXqaRj8L9i3n2plDe4l91HoXfu+JN6XQG1mFPvx6yhqz0J3CVZ8EdovByoY40NUpiooqkZruYyUXQwkwBFWFlI5BgEBeIGA+F7kFd8JpSWCvrIT9twN7+6kNrMC54pHKSxe/Z4k3pOAZdSYfWQzHTwK3UVYdSN0nsv4tld55qe/Zsfru6jM1FEUic7uds6/+iLOu1bEQ2tEQngjlpD4/VhC7/EqJCgIehmo7Yc998DgLubq60he+3P0QscxSbwngdEt36Y8fBv0pGDFddC5ntef+BX3f+cxbE9l1bmnctKa5Zh1k11bdzC8cw9rz1/KpltvJNfRA66QyJExIGqDBLLwTkxESEq8F3HigGGDrUF1Hwz8CN49wFjm83Rccw+yEieAQ1yOSeDgH5+jtOVilB4P+i+B/iuojI/yna/+N+19PfzF166mq78TEAUovOnO1/aw5X9+ycar13LGpeeBbUW/IkiI+BBa98GrUxkd4+DIFNXZeUzDQJIksgWdriWtFLvawE+GJGbegV0Pw946Y2fdT9c5f3WUF44iYDdqzP3kE7Qln4OTlkHvlZBOY9RrDA5VWH1WH7KeBMcILayKzCOOQiAZY66Grsf6j8CLywkF16jx6J2P8cLjb2KaImtJJJIqijgVmUIxxRnn93PpX56DpuvQkODA8zDwPJXpPpS/fpFMSdSY9/HA0PP30fPG9UhL0rD0o1DoBdmClAoFHWwbHA+SCdAk9ry4m2d/toO+la1csvk80HNgClnEMRDVAEXCtWyefmQ78wdNepYXyBQzaLqCmpRxLJfKbAN8h1UrCqRUOWg/MGow+CS8O8ZI/+0svvzv35uAbdaZ+dGldKgvQt8KWLQOVGE9CRJy+CpuLLSYVRnaOsQ9tz3DyJ4GqQx84baNrLvmTLBEMArgTTEgWi5xD1XHnJxkcv8s3Se3gZYBX8gtIir+d8YA0wbXBycBBwdgzyvMHCyTvOF1MqWuBRKHSWhi25MUfnk52hINej8ChU5IeBH4JgKaEkj/p//8MlufHGTF8iTD+yyWb+hn8zc3hsCdIwgIVWkSA8/u4cHvvsLI3gYbLu9m083noGVTICTleuH/BcCj9zZQr8PwVty9Bxk76z9ZfMGNxyaw/2dfp3v4e9DTBeXVoKuhxWPrxx7QE9hVk3u/9QrG+BRLujX2DTUor+3lun9YH95cgGk+kgrmTJ27/+459v5hmmJRYt6Q+NI3z2H5+YuhYoXAF8CLCu2Fp+XD5F7YO8CB1FWUb3gUKagzsOCBxvxBpu+9kLK+E6m7D1oWgSZcrkQkhJQiL2gKjuvzk398g8rQBO0dKsP7bS78/Bmc++l+qEWSaIphMip7X57gvttfp7Pgkc7I7BtxufJLp3H6pYthzjxEQJAQlhdesFywfZibxR/ZxcHJAtpnXyBfDtuMBQJTu1/Bf3ADxcUJEuVeyOUg2URAlcIcHsQDkNd45Zfj/PbH21E8lyXrurnyptVkM35osYUaFkkpp7LrdxP877++TXdHeHl8RuLavz2NvtMLkQeE52IvCBKCQOSFegN3fJja8ByzGx+k57zPHE5g9NWH0J7aRGZRhmRHF1ImDcnI+oGMmsAL74nMkdDY/kYNo+awZn2JbN6Huh2kx8OKsPgpTaYy5XL/HdswD8wFIdt5aifX3LSclOKC6YW9ngh+EQfC6sIDMQnDwj44QWNkmqk1t9N3ZZiNFjww9Kt/IvfmraTLWZKtrchpPUyVwvJB9ogICC+I2iVMKCSWT4XXDSsMxCB7NrcQsY58SCfZ9nKVVx4fodie4sJrFgXjBPOiajdbX8gnJhJ6wW80sKanaRyYZabny/RtuutwAnvu+yLFkXvRy1m0Ug5Fz0BSBHFs+Ug6AQGRSiOZxOleFi2CEhEQ15pJRJYVf9I1GoZMUvGRJQuqFnjSIQJBFmomICTp4hl1rEqFxmiFg4VPsfTGh5FkJfKA77Prnk9TmnqEdFcarZQhoWchqTUREF1DUxzExVYAF8CCz4JExOgI/Ig6EKT6qB+KLRyPCkFrFFk9joMgkAUBG9eoYlZqGOM1ppRzOenLW1D1XEhAzPUD/341+ZnHyXTppIppEuKipjdlIAEuIiHiIbB4JKUYvBR75chONMqnAYlobgiAN32OQQczT+SFKA58y8Q15jHn6tTGDaalM1l6069R08VDMbDjPzZRGH+IdJdOupgikckhJzNhrxMEsB91ksIL4r18iEBg+cgDMYkgwmLg0YfYCwJ8cIrmLiKyMB5EWciNiqHt4lt1HKNCY7ZBbcJgVr+IZV95gkRSP0Rg5wNfJbPrTjJdKdJFFTWTRdbySEJGQfqMGs+gHY4kc5gXhJTEICO8cOSmJtK44LEAPJJU87QpCMXWF+1UkI1sPKuCXatizNnUxuvMd32GVV98ILDQQhba+/S/4T5zE7lyikxJRctoyKkcsppt8oIAHgVoQCLSvyAXp9rmFBpbN3gVw35EYOG9WLk0jQPi79HYHA5uHr5ZxbPmsWoN6rMO1bE61sk3s3zTd4+oA288wdyDV1EoJ8m0JNGzCRQtjZzM4as6tu0gqQnUbCKcX2PZyBKe41CdbVCZs3AcH1mRSKgymWyCbCGJklPDmiJAibweyyX2Rpz/m+b+oBWxzQC8Z9Zo1GxqMw7zowbyxXfSf9nfHE5gdv929t15NsVWk0xrinQuQVJXkbUslpXAVYsBSDWtUConQ08EDZrMO7+f48WXZ9EyCZIpBdf2scVs7PokFJ9SxqfcrtLbn6G1Jxd2s43YI1EcxJYPSAjwFr5Tw7dq2A2TetWlNm0xN+ZQ+uwvWHT6ZYcTcCyD7T/8JJnZZ8h0ZkjnE+hpBSWZolH30FeeS31imqG39tJ7RplgbSMsqMC0IVN3ZIpFhaQqBWqxHJ+66TNb9ZgYMdi3Zw5z1qCgOpy1rkTf6lJYbeNtxkL2EalUgDfAqeGaDUzDpTbvUJ80mPOWsfLmF0jlxB6qqRKLD7t+/i+4z36DbJCJVNJZMWwkMGsuWv+pJLQE2361g2Jfme4VaiiFuCKLOBCWi7voOOUGsZEAL8H4lMtbW2doz3isPTUbEYiyUFwHXDsCX8e3G1imExiwNmdTH6thrvwSp9xw97Hb6Zl92xj6wQayuTrZNj2QkZiYrHkTre9kkq0tbHv8TfT2TpaeoocBKDJOczZqLgFxGg1eo2FIU8HwoC6WYXEajYLcFS21ie/WwW5g2y5mw8OYd6hOm1Qnbdq/sIXyqR87NgHx1233fA5l4MfoHWmyRZVUOoFjWGRWb0Bta+f39z9Fpq3EyjPEJCUCWmSiKB4C8FF9EBnR9bAMJwhA1/JQ9STZnIoivBUvIoIs5IAAHyyBDXyngWt7mJZPw3CozTkYkzWMwvmc8rUtQf6Pj6OG+vGB1xn+4YXkSzZ6i46elkkoMvl1n0Lu7GP3Tx/ArovhvhXkJEhq2AMFOd7DdWwc28UyRQpXcJUMnlogmU7C7F6M+QZti0skxfrddcGzw9VkYH0B3sIRScD2g8G/XnWoz5jMTzi0bn6E3rP/7LA56Zhrld0PfIXqa3eT6UiR0iHflqewYTN0rsUeeI7plx4hl3XQcplgG+fLKr6UxCWJr+Yg04qULqBqEkpSSMWAuWkm3hmkUtHoXtFCShOAnZCAK4YZIR0XxxHEfSzLpVF3qVVsahMN1N6PsfIrjyInUscn4M4OsO07l4E9Qq7g0bmqn8z6z+FnepHcOt7wa1hju8LCm0wjpXJIehFSRRRNRmIGjP0wOszowBTjwwbVCqRbCixbkyNfENaPwAfysfFcL9hKijpiWV6QeepVm/q0hVFPc/otP0ddvOEw8Edloear9R0P8dYPPkdHp0XvhjNJrPw4fqoL5ETQxgq3+yJfygKwADEPlUH8A28x++4wA+8Y7B1KUrd02soplq1M0NcnkdIdcITVw+Wu7/l4rh+Cd31sAb7hBkNSo2JROeiz4ro7KG645Sjw70tAXJx8+lvMv/RdTrpoDVLHGjyx90xkQYkeCYmlrDhdA8meQxrfyugLr/HoL/IMjrWycpXEOWf7LF/mkcqKVYvYNISvogMW4EXmFaEQgg91b9YdGlWL+UmP8oU3sPjTdzU9Vzicx/uv1z2HmUc+Rr7dRG7twxdBK6sQPNMSDVq06w/W5D5SbYLG7jd57dkKg6MFzlvvs3ypi6T44TOPuHsW2TPg4eOI0wmD1mq4WA0Hs2ozP+XSse4aej5zD6j5qGoeOWQcUciO5SPjiStQU6MopTK+yDhHdZrR3jPoDB2k6jD7X9vH4P4Ma06RKRXcqO0PO1JReAVwcboxeMsLpGObDmbFpjLjs+iCzXT/+fcgWXxP8MeVkMjP1ccuQ0uNkCh14CviQYTI21E3FjRjwpRhQMqeiTV6gN88aWOQ54L1Num0kEuIIegaIuACvOt4OLaHY3k4Qb53MRoa/Z/6Om0bbwVFe1/wxycAVLdcT6L6DFpbayihuFcQBUwQWcjhIh58fKPB7rdrDA5rrDkF8jk3kEtYJnxcN7S+5/gheNOlUXOoVSDbeyZ9V9+KvvzqSAzN271jxvCheeDYl6G69S54+1b0jmyQgcLKGz19DLKIsL6IhbBdkH2f8cEqfxxQ6F4CuUwkIdEmxdKxhVy8oMdp1EFtW8aSi6+nZd3nkXSxtg/oHvGA5EMSMCfeYf7hjWRbXGTRx8hC6/G9JfwgMsNxUnI9jKk6zz+vYDkyq5cZQTsdzCmutFBhbVtCUtPkek+n65xrya++Ejnf3wQ8NMaJHMd9yCduMv27O5Devp1UQQ3HSxGwwTQWnuIBReAV0UbPGvxhe4aK00JemUKWPRQtRSpTQMu3orV0k+tbS37Zhaitq0FU7oXjxKzeTOyECAiZzPz229g770X1ppATUviUKAAvglMK2xpPQe34CNn1t0GuF2d+Es/3g/WHopeQBFgxoh51fHDg8S1OiED8ZXNiO+bY27hzQ7jVsWDcE3VBy3egt/egFJeitK+N8vbxBPDhQR/uAVFBxMDxJ3p8IA/8f+T4J0/g/wAdf6rpHzLg8wAAAABJRU5ErkJggg=='
}

export function getTokenLogoURI(symbol?: string, isDex?: boolean): string {
  if (!symbol) return getDumbLogoURI()
  return `${(isDex) ? DEX_TOKEN_LIST_BASE_URI : BRIDGE_TOKEN_LIST_BASE_URI}/assets/asset_${symbol}.svg`
}

export function unwrapTokenSymbol(symbol: string): string {
  return (symbol === `W${DEFAULT_TOKEN_SYMBOL}`) ? DEFAULT_TOKEN_SYMBOL : symbol
}

export function getPrioritySymbol(currency?: Currency): string {
  if (!currency) return '-'
  return (currency.address === ethers.constants.AddressZero) ? currency.symbol : currency.address
}

/**
 * Validates an address and returns the parsed (checksummed) version of that address
 * @param address the unchecksummed hex address
 */
export function validateAndParseAddress(address: string): string {
  try {
    return ethers.utils.getAddress(address)
  } catch (error) {
    throw new Error(`${address} is not a valid address.`)
  }
}

export const getContract = (
  address: string,
  abi: ContractInterface,
  provider: Provider | Web3Provider,
): Contract => new Contract(address, abi, provider as Provider)

export function sleep(ms: number): Promise<unknown> {
  // eslint-disable-next-line no-promise-executor-return
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function timeout(prom: Promise<unknown>, time: number): Promise<unknown> {
  // eslint-disable-next-line no-promise-executor-return
  return Promise.race([prom, new Promise((_r, rej) => setTimeout(rej, time))])
}

export function getPriceForCurrency(currency: Currency, value: BigNumberish, price: number): number {
  const convertedValue = +formatUnits(value, currency.decimals)
  return +((convertedValue * price).toFixed(4))
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createStorage(dbName: string): any {
  const db = localForage.createInstance({
    name: dbName,
  })
  return {
    db,
    getItem: db.getItem,
    setItem: db.setItem,
    removeItem: db.removeItem,
  }
}

export function getDefaultDeadline(): number {
  return Math.floor(Date.now()) + 60 * 60 // for 1 hour
}

// delta in minutes
export function getDeadline(delta: number): number {
  return Math.floor(Date.now()) + 60 * delta
}

export function quote(amount0: BigNumber, reserve0: BigNumber, reserve1: BigNumber): BigNumber {
  if (amount0.lte(ethers.constants.Zero)) {
    throw Error('Insufficient amount')
  }
  if (reserve0.lte(ethers.constants.Zero) || reserve1.lte(ethers.constants.Zero)) {
    throw Error('Insufficient liquidity')
  }
  return amount0.mul(reserve1).div(reserve0)
}

export function getGovernanceInfo(chainId?: number): {
  currency?: Currency,
  staker?: Currency,
} {
  const chain = getChainData(chainId)

  return (chain?.governance) ? {
    currency: new Token(
      chain.chainId,
      chain.governance.tokenAddress,
      18,
      'KSG',
      'KirkaSwap',
    ),
    staker: new Token(
      chain.chainId,
      chain.governance.stakingAddress,
      18,
      'KSG',
      'KirkaSwap',
    ),
  } : {
    currency: undefined,
    staker: undefined,
  }
}

const diffTime = (date: Date): number => {
  const today = new Date()
  return Math.abs(today.getTime() - date.getTime())
}

export const getTimeRemaining = (ts: number): {
  total: number,
  days: number,
  hours: number,
  minutes: number,
  seconds: number,
} => {
  const endDate = new Date()
  endDate.setTime(ts * 1000)
  const total = diffTime(endDate)
  const seconds = Math.floor((total / 1000) % 60)
  const minutes = Math.floor((total / 1000 / 60) % 60)
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24)
  const days = Math.floor(total / (1000 * 60 * 60 * 24))

  return {
    total,
    days,
    hours,
    minutes,
    seconds,
  }
}

export const getTimeRemainingStr = (ts: number): string => {
  const dateInfo = getTimeRemaining(ts)
  if (dateInfo.days) {
    return `${dateInfo.days} days, ${dateInfo.hours} hours, ${dateInfo.minutes} minutes`
  }
  if (dateInfo.hours) {
    return `${dateInfo.hours} hours, ${dateInfo.minutes} minutes`
  }
  if (dateInfo.minutes) {
    return `${dateInfo.minutes} minutes`
  }
  if (dateInfo.seconds) {
    return `${dateInfo.seconds} seconds`
  }
  return ''
}

export const getNowTs = (): number => Math.round(Date.now() / 1000)

export const getPrettyDate = (timestamp: number, hideYear?: boolean, hideTZ?: boolean): string => {
  const nowDate = new Date()

  const newDate = new Date()
  newDate.setTime(timestamp * 1000)

  const zeroAdd = (d: number) => (`0${d.toString()}`).slice(-2)

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ]
  const monthName = monthNames[newDate.getUTCMonth()].slice(0, 3)

  const yearStr = (!hideYear || (hideYear && nowDate.getUTCFullYear() > newDate.getUTCFullYear()))
    ? `${newDate.getUTCFullYear()} ` : ' '

  return `${monthName} ${newDate.getUTCDate()}, ${yearStr}`
    + `${zeroAdd(newDate.getUTCHours())}:${zeroAdd(newDate.getUTCMinutes())}${hideTZ ? '' : ' UTC'}`
}

export const getSighash = (fname: string): string => ethers.utils.id(fname).slice(0, 10)

export const parseBigNumber = (arg: string): BigNumber => (
  BigNumber.from((arg === '0x') ? ethers.constants.Zero : arg)
)

export function truncate(str: string, maxDecimalDigits = 18): string {
  if (str.includes('.')) {
    const parts = str.split('.')
    return `${parts[0]}.${parts[1].slice(0, maxDecimalDigits)}`
  }
  return str
}
